// ─── Email preview, on a phone or on a computer ───────────────────────────────
// One preview frame, shared by the campaign editor, the journey step editor and
// the mail-template editor, with a Phone / Computer switch.
//
// Why it matters: an email is laid out for ~600px and read on a ~390px screen,
// and the two really do differ — a Mailchimp export carries `@media` rules that
// only fire on a narrow viewport, and clients recover from imperfect markup
// differently at different widths. A campaign went out looking right on a laptop
// and wrong on a phone with nobody able to see it beforehand, because the only
// preview we offered was full-width.
//
// The switch is honest rather than cosmetic: an iframe gets its OWN viewport, so
// narrowing it to a phone's width makes the email's mobile media queries fire
// exactly as they will in the inbox. Nothing is scaled or faked.
//
// Phone leads by default — clients read on their phones (see CLAUDE.md).
import { useState } from 'react';
import Icon from './Icon.jsx';

const PHONE_W = 390; // iPhone 15/16 CSS width — the common case, not the smallest

const seg = (on) => ({
  display: 'inline-flex', alignItems: 'center', gap: 5, border: 0, borderRadius: 7,
  padding: '4px 11px', fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
  background: on ? 'var(--card)' : 'transparent', color: on ? 'var(--text)' : 'var(--muted)',
  boxShadow: on ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
});

export default function EmailPreviewFrame({ html, title = 'Email preview', height = 460, initial = 'phone', right = null }) {
  const [view, setView] = useState(initial);
  const phone = view === 'phone';
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <div style={{ display: 'inline-flex', gap: 2, padding: 2, background: 'var(--bg)', border: '1px solid var(--hairline)', borderRadius: 9 }}>
          <button type="button" style={seg(phone)} onClick={() => setView('phone')} aria-pressed={phone}>
            <Icon name="device" size={13} />Phone
          </button>
          <button type="button" style={seg(!phone)} onClick={() => setView('desktop')} aria-pressed={!phone}>
            <Icon name="desktop" size={13} />Computer
          </button>
        </div>
        <span style={{ fontSize: 11.5, color: 'var(--muted)' }}>{phone ? `${PHONE_W}px — a real phone viewport, so the email's mobile rules apply` : 'Full width'}</span>
        {right}
      </div>
      {/* The frame is narrowed, never scaled: the email must lay itself out at a
          phone's width, which is the whole point of looking. */}
      <div style={{ display: 'flex', justifyContent: phone ? 'center' : 'stretch', background: phone ? 'var(--bg)' : 'transparent', borderRadius: 12, padding: phone ? 10 : 0 }}>
        <iframe
          title={title} srcDoc={html} sandbox="" referrerPolicy="no-referrer"
          style={{
            width: phone ? PHONE_W : '100%', maxWidth: '100%', height, background: '#fff',
            border: '1px solid var(--hairline)', borderRadius: phone ? 18 : 12, display: 'block',
          }}
        />
      </div>
    </div>
  );
}
